import { useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Candidate {
  id: number; name: string; email: string; roleType: string; stage: string; hiredUserId: number | null;
}

const STAGES = ["applied", "screening", "interview", "reference", "offer", "hired", "rejected"];
const stageChip: Record<string, string> = {
  applied: "bg-slate-100 text-body", screening: "bg-sky-50 text-sky-700",
  interview: "bg-sky-50 text-sky-700", reference: "bg-amber-50 text-amber-700",
  offer: "bg-amber-50 text-amber-700", hired: "bg-teal-50 text-teal-700", rejected: "bg-red-100 text-red-700",
};

export default function RecruitmentSection() {
  const { data, reload } = useFetch<Candidate[]>("/v1/workforce/candidates");
  const [form, setForm] = useState({ name: "", email: "", roleType: "worker" });
  const [error, setError] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    await api("/v1/workforce/candidates", { method: "POST", body: JSON.stringify(form) });
    setForm({ name: "", email: "", roleType: "worker" }); reload();
  }
  async function moveStage(id: number, stage: string) {
    const note = stage === "applied" ? undefined : window.prompt(`Note for "${stage}" (optional):`) ?? undefined;
    await api(`/v1/workforce/candidates/${id}/stage`, { method: "POST", body: JSON.stringify({ stage, note: note || undefined }) });
    reload();
  }
  async function hire(id: number) {
    const password = window.prompt("Set a temporary password for the new employee (min 10 chars):");
    if (!password) return;
    setError(null);
    try {
      await api(`/v1/workforce/candidates/${id}/hire`, { method: "POST", body: JSON.stringify({ password }) });
      reload();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">Recruitment</h2>
      <form onSubmit={add} className="card p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <label className="block sm:col-span-2"><span className="label">Candidate name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></label>
        <label className="block sm:col-span-2"><span className="label">Email</span>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" /></label>
        <label className="block"><span className="label">Role</span>
          <select value={form.roleType} onChange={(e) => setForm({ ...form, roleType: e.target.value })} className="input">
            <option value="worker">Worker</option><option value="coordinator">Coordinator</option>
          </select></label>
        <button className="btn-dark sm:col-span-5 justify-self-start">Add candidate</button>
      </form>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-edge-soft">
            <th className="th">Candidate</th><th className="th">Role</th><th className="th">Stage</th><th className="th"></th>
          </tr></thead>
          <tbody>
            {(data ?? []).map((cand) => (
              <tr key={cand.id} className="border-b border-edge-soft last:border-0">
                <td className="px-5 py-3"><span className="font-medium text-ink">{cand.name}</span><span className="text-xs text-faint block">{cand.email}</span></td>
                <td className="px-5 py-3 capitalize">{cand.roleType}</td>
                <td className="px-5 py-3">
                  <select value={cand.stage} onChange={(e) => moveStage(cand.id, e.target.value)}
                    disabled={cand.stage === "hired"} className={`chip border-0 ${stageChip[cand.stage]}`}>
                    {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3 text-right">
                  {cand.stage !== "hired" && cand.stage !== "rejected" &&
                    <button onClick={() => hire(cand.id)} className="text-sm text-teal-700 hover:underline">Hire</button>}
                  {cand.hiredUserId && <span className="text-xs text-teal-700">✓ employee</span>}
                </td>
              </tr>
            ))}
            {(data ?? []).length === 0 && <tr><td className="px-5 py-6 text-mut" colSpan={4}>No candidates yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
